import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

const BASE = () => process.env.REACT_APP_BACKEND_URL || "";
const SESSION_KEY = "wa_ai_session";

const HIDDEN_ON = ["/admin", "/login", "/register", "/wachtwoord", "/billing", "/uitschrijven"];

const SUGGESTIONS_DEFAULT = [
  "Hoe werken huurwoningen in Nederland?",
  "Wat zijn rode vlaggen in advertenties?",
  "Welke documenten heb ik nodig?",
  "Hoe schat ik een realistische huurprijs?",
];
const SUGGESTIONS_LISTING = [
  "Beoordeel deze advertentie",
  "Is deze prijs realistisch?",
  "Wat moet ik vragen tijdens bezichtiging?",
  "Rode vlaggen in deze listing?",
];

// ---- Context so other pages can push context (listing_id, filters) ----
const AIContext = createContext({ setPageContext: () => {}, open: () => {} });
export const useAIChat = () => useContext(AIContext);

const Bubble = ({ role, children, pending }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
        role === "user" ? "bg-brandblue text-white" : "bg-slate-100 text-navy"
      } ${pending ? "animate-pulse" : ""}`}
    >
      {children}
    </div>
  </div>
);

const ChatPanel = ({ onClose, context }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const sessionId = useRef(sessionStorage.getItem(SESSION_KEY) || "");
  const scrollRef = useRef(null);

  const suggestions = context?.listing_id ? SUGGESTIONS_LISTING : SUGGESTIONS_DEFAULT;

  useEffect(() => {
    fetch(`${BASE()}/api/ai/chat/quota`, {
      headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {},
    }).then((r) => r.json()).then(setQuota).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setBusy(true);
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "", pending: true }]);

    const token = (() => { try { return localStorage.getItem("wa_token"); } catch { return null; } })();
    try {
      const res = await fetch(`${BASE()}/api/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: q,
          session_id: sessionId.current || undefined,
          context,
        }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ detail: "Assistent tijdelijk niet beschikbaar" }));
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: err.detail || "Er ging iets mis." };
          return copy;
        });
        setBusy(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          let evt = "message";
          let data = "";
          for (const l of lines) {
            if (l.startsWith("event:")) evt = l.slice(6).trim();
            else if (l.startsWith("data:")) data += l.slice(5).trim();
          }
          if (!data) continue;
          try {
            const parsed = JSON.parse(data);
            if (evt === "session" && parsed.session_id) {
              sessionId.current = parsed.session_id;
              sessionStorage.setItem(SESSION_KEY, parsed.session_id);
            } else if (evt === "error") {
              acc += `\n\n⚠️ ${parsed.message || "Error"}`;
            } else if (parsed.t) {
              acc += parsed.t;
            }
          } catch { /* ignore */ }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: acc, pending: false };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Verbinding verbroken. Probeer opnieuw." };
        return copy;
      });
    } finally {
      setBusy(false);
      // Refresh quota
      fetch(`${BASE()}/api/ai/chat/quota`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then((r) => r.json()).then(setQuota).catch(() => {});
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-navy/70 backdrop-blur-sm sm:items-end sm:justify-end sm:p-4" role="dialog">
      <div className="mt-auto flex h-[90vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:mt-0 sm:h-[640px] sm:max-h-[85vh] sm:w-[420px] sm:rounded-3xl" data-testid="ai-chat-panel">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 bg-navy px-4 py-3 text-white">
          <div className="flex items-center gap-2 min-w-0">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-300/20 text-amber-300"><Sparkles size={16} /></span>
            <div className="min-w-0">
              <div className="truncate font-display text-sm font-bold">WoningAlert Assistent</div>
              <div className="truncate text-[11px] text-white/60">
                {context?.listing_id ? "In context van deze woning" : "Vraag mij alles over wonen"}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Sluit chat" className="rounded-full p-1.5 hover:bg-white/10" data-testid="ai-chat-close">
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="ai-chat-messages">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brandblue-light text-brandblue"><Sparkles size={24} /></span>
              <h3 className="mt-3 font-display text-base font-bold text-navy">Hoi! Hoe kan ik je helpen?</h3>
              <p className="mt-1 max-w-xs text-xs text-slate-500">
                Ik ben de WoningAlert Assistent. Ik help met vragen over wonen, huren, kopen en woningen op het platform.
              </p>
              <div className="mt-5 grid w-full gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="rounded-xl border border-border bg-white px-3 py-2 text-left text-xs font-medium text-slate-700 hover:border-brandblue hover:text-brandblue"
                    data-testid={`ai-suggest-${s.slice(0, 10)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <Bubble key={i} role={m.role} pending={m.pending && !m.content}>
                {m.content || (m.pending ? <Loader2 className="animate-spin" size={14} /> : "")}
              </Bubble>
            ))
          )}
        </div>

        {/* Input */}
        <div className="border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {quota && !quota.unlimited && (
            <div className="mb-1.5 text-[10px] text-slate-400">
              {quota.remaining}/{quota.cap} berichten vandaag over
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Stel een vraag…"
              className="h-11 rounded-xl"
              disabled={busy}
              data-testid="ai-chat-input"
            />
            <Button type="submit" size="icon" disabled={busy || !input.trim()} className="h-11 w-11 shrink-0 rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="ai-chat-send">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

/**
 * Mounts a floating "Vraag WoningAlert AI" launcher in the bottom-right that
 * opens a mobile-first chat panel. Auto-detects context from the URL:
 * - /woning/:id or /koopwoning/:id → sends listing_id
 * - /woningen or /koopwoningen → sends filters from URLSearchParams
 *
 * Call `useAIChat().setPageContext({...})` to override from any page.
 */
export default function AIChatLauncher() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [override, setOverride] = useState(null);

  // Derive context from the URL
  const autoCtx = useMemo(() => {
    const p = location.pathname;
    const ctx = {};
    const matchRental = p.match(/^\/woning\/([^/]+)/);
    const matchSale = p.match(/^\/koopwoning\/([^/]+)/);
    if (matchRental) ctx.listing_id = matchRental[1];
    else if (matchSale) ctx.listing_id = matchSale[1];
    if (p === "/woningen" || p === "/koopwoningen") {
      const qs = new URLSearchParams(location.search);
      const f = {};
      for (const k of ["city", "min_price", "max_price", "min_rooms", "max_rooms"]) {
        const v = qs.get(k);
        if (v) f[k] = v;
      }
      f.mode = p === "/koopwoningen" ? "koop" : "huur";
      if (Object.keys(f).length) ctx.filters = f;
    }
    return ctx;
  }, [location]);

  const context = override || autoCtx;

  const setPageContext = useCallback((c) => setOverride(c), []);
  const openChat = useCallback(() => setOpen(true), []);

  // Hide launcher on admin & auth pages
  const hidden = HIDDEN_ON.some((p) => location.pathname.startsWith(p));

  return (
    <AIContext.Provider value={{ setPageContext, open: openChat }}>
      {!hidden && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Vraag WoningAlert Assistent"
          className="fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full bg-navy px-4 py-3 text-sm font-semibold text-amber-300 shadow-xl hover:bg-navy/90 sm:bottom-6"
          data-testid="ai-chat-launcher"
        >
          <MessageCircle size={18} />
          <span className="hidden sm:inline">Vraag WoningAlert Assistent</span>
        </button>
      )}
      {open && <ChatPanel onClose={() => setOpen(false)} context={context} />}
    </AIContext.Provider>
  );
}
